/**
 * Tipo de embarcação: do código do AIS para o português — funções puras
 * (testadas em `kinds.test.ts`).
 *
 * O campo `shiptype` da mensagem estática do AIS (tipo 5) é um número de 0 a
 * 99 em que a DEZENA diz a categoria e a unidade um detalhe de carga que quase
 * ninguém preenche direito. O AmaView usa só a dezena: agrupar em 8 famílias é
 * o que cabe numa legenda e é o que a pessoa de fato quer saber — "isso é um
 * navio de carga ou uma balsa de passageiros?".
 *
 * A tabela completa está na ITU-R M.1371-5, tabela 53.
 */

/** Famílias que o mapa desenha e a legenda explica. */
export type Familia =
  /** Carga seca e contêineres (dezena 7). */
  | 'carga'
  /** Petroleiros, graneleiros líquidos e químicos (dezena 8). */
  | 'tanque'
  /** Passageiros: navios de linha, catamarãs, cruzeiros (dezena 6). */
  | 'passageiros'
  /** Pesca (30). */
  | 'pesca'
  /** Rebocadores, empurradores e os comboios de balsa que eles tocam (31, 32, 52). */
  | 'reboque'
  /** Serviço: prático, salvamento, dragagem, fiscalização, militar (33–35, 50–59). */
  | 'servico'
  /** Recreio e vela (36, 37) e embarcações rápidas (dezena 4). */
  | 'recreio'
  /** Sem tipo declarado ou fora das famílias acima. */
  | 'outro';

export interface Tipo {
  familia: Familia;
  /** Nome curto, para o popup e a lista. */
  rotulo: string;
}

/** Códigos que valem por si, antes de cair na dezena. */
const EXATOS: Readonly<Record<number, Tipo>> = {
  30: { familia: 'pesca', rotulo: 'Pesca' },
  31: { familia: 'reboque', rotulo: 'Rebocador' },
  // 32 é o rebocador de comboio grande (> 200 m ou > 25 m de boca) — no
  // Amazonas é o empurrador de balsas de minério e de combustível.
  32: { familia: 'reboque', rotulo: 'Empurrador de comboio' },
  33: { familia: 'servico', rotulo: 'Draga' },
  34: { familia: 'servico', rotulo: 'Mergulho' },
  35: { familia: 'servico', rotulo: 'Militar' },
  36: { familia: 'recreio', rotulo: 'Veleiro' },
  37: { familia: 'recreio', rotulo: 'Recreio' },
  50: { familia: 'servico', rotulo: 'Prático' },
  51: { familia: 'servico', rotulo: 'Busca e salvamento' },
  52: { familia: 'reboque', rotulo: 'Rebocador' },
  53: { familia: 'servico', rotulo: 'Serviço de porto' },
  54: { familia: 'servico', rotulo: 'Combate à poluição' },
  55: { familia: 'servico', rotulo: 'Fiscalização' },
  58: { familia: 'servico', rotulo: 'Transporte médico' },
  59: { familia: 'servico', rotulo: 'Embarcação especial' },
};

/** Categoria pela dezena, quando o código exato não diz nada de especial. */
const DEZENAS: Readonly<Record<number, Tipo>> = {
  2: { familia: 'outro', rotulo: 'Veículo de efeito solo' },
  3: { familia: 'servico', rotulo: 'Serviço' },
  4: { familia: 'recreio', rotulo: 'Embarcação rápida' },
  5: { familia: 'servico', rotulo: 'Serviço' },
  6: { familia: 'passageiros', rotulo: 'Passageiros' },
  7: { familia: 'carga', rotulo: 'Carga' },
  8: { familia: 'tanque', rotulo: 'Tanque' },
  9: { familia: 'outro', rotulo: 'Outro' },
};

const DESCONHECIDO: Tipo = { familia: 'outro', rotulo: 'Tipo não declarado' };

/**
 * Traduz o código do AIS. `null`, 0 e qualquer coisa fora de 0–99 viram
 * "não declarado" — e isso é comum: boa parte da frota transmite 0.
 */
export function tipoDe(codigo: number | null | undefined): Tipo {
  if (codigo === null || codigo === undefined || !Number.isFinite(codigo)) return DESCONHECIDO;
  const n = Math.trunc(codigo);
  if (n < 20 || n > 99) return DESCONHECIDO;
  return EXATOS[n] ?? DEZENAS[Math.trunc(n / 10)] ?? DESCONHECIDO;
}

/** Nome da família na legenda e nos filtros. */
export const NOME_FAMILIA: Readonly<Record<Familia, string>> = {
  carga: 'Carga',
  tanque: 'Tanque',
  passageiros: 'Passageiros',
  pesca: 'Pesca',
  reboque: 'Reboque e comboio',
  servico: 'Serviço',
  recreio: 'Recreio',
  // A maior "família" na Amazônia, e por um motivo que vale dizer: boa parte
  // da frota comercial brasileira deixa o campo de tipo no padrão de fábrica.
  // "Outro" sugeriria uma categoria; "Não declarado" diz a verdade.
  outro: 'Não declarado',
};

/** Ordem em que as famílias aparecem na legenda e nos filtros. */
export const FAMILIAS: readonly Familia[] = [
  'carga',
  'tanque',
  'reboque',
  'passageiros',
  'pesca',
  'servico',
  'recreio',
  'outro',
];
